/*
 * Global shop header cluster — floats top-right on every page except /studio/*.
 * Two icon buttons: Cart (opens cart drawer) + Account (sign-in when signed
 * out, dropdown with Library/Sign-out/Delete when signed in).
 *
 * Mobile: cart stays in the top-right. Account button moves into the menu
 * drawer (rendered by <NewSiteHeader> — see `MobileAccountMenuItem` below).
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ShoppingCart, Trash2, ArrowRight, X, User, Library, LogOut, ShieldOff, UserPlus,
} from "lucide-react";
import { useCart, formatMoney } from "../../context/CartContext";
import { useShopAuth } from "../../context/ShopAuthContext";

const BRAND_BLUE = "#3b82f6";
const BRAND_PINK = "#ec4899";
const HIDDEN_PATH_PREFIXES = ["/studio", "/admin"];
const MOBILE_BP = 768;

export default function ShopHeaderCluster() {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < MOBILE_BP : false));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BP);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  if (HIDDEN_PATH_PREFIXES.some((p) => location.pathname.startsWith(p))) return null;

  // Landing (`/`) has no F logo in the top-right corner, so the cluster
  // gets to sit closer to the edge (aligned with where the F would be:
  // right:50, matching the Talk-to-Flux button in the bottom-right).
  // Every other page renders an F logo per-page, so give it 140px of gutter.
  const isLanding = location.pathname === "/";

  // Desktop: cart + account. Mobile: cart only (account renders inside menu).
  // NB: The LumenForge F logo is rendered per-page (Marketplace has its own,
  // Library/Admin add their own). Do NOT render it here — that duplicates it
  // on pages that already ship one. Give a wide gutter so the SIGN IN pill
  // never crashes into the logo (F is ~40px + right:28 gutter, so anchor
  // this cluster at right:96 desktop to leave 68px between the pill and
  // the logo's leading edge).
  return (
    <div
      data-testid="shop-header-cluster"
      style={{
        position: "fixed",
        top: isMobile ? 20 : 18,
        // Landing page: right-align with the "Talk to Flux" chat FAB
        // (`right: 50`) so the top-right cart and bottom-right chat form
        // a visual pair on the same vertical axis.
        // Other pages: hug the F header logo (right: 14, width 48) with
        // an 8px gap. Desktop keeps the wider gutter on non-landing.
        right: isMobile ? (isLanding ? 50 : 68) : (isLanding ? 50 : 140),
        zIndex: 45,
        display: "flex", gap: 14, alignItems: "center",
      }}
    >
      <CartButton isMobile={isMobile} />
      {!isMobile && <AccountButton isMobile={isMobile} />}
      {/* Vertical divider between the shop cluster and the page-level F logo. */}
      {!isMobile && (
        <span aria-hidden="true" style={{
          display: "inline-block", width: 1, height: 22,
          background: "rgba(0,0,0,0.14)",
          marginLeft: 4,
        }} />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// CART
// -----------------------------------------------------------------------------
function CartButton({ isMobile }) {
  const navigate = useNavigate();
  const cart = useCart();
  const panelRef = useRef(null);
  const size = isMobile ? 48 : 40;

  useEffect(() => {
    if (!cart.drawerOpen) return;
    const onDoc = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) cart.closeDrawer();
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [cart]);

  return (
    <div style={{ position: "relative" }}>
      <IconButton
        testid="cart-open-button"
        onClick={() => (cart.drawerOpen ? cart.closeDrawer() : cart.openDrawer())}
        label="Open cart"
        size={size}
      >
        <ShoppingCart size={18} strokeWidth={2.2} />
        {cart.items.length > 0 && (
          <Badge testid="cart-badge">{cart.items.length}</Badge>
        )}
      </IconButton>
      {cart.drawerOpen && (
        <Drawer refEl={panelRef} testid="cart-drawer" size={size} isMobile={isMobile}>
          <DrawerHeader label={`Cart (${cart.items.length})`} onClose={cart.closeDrawer} testidClose="cart-close-button" />
          {cart.items.length === 0 ? (
            <p data-testid="cart-empty" style={{ fontSize: "0.9rem", color: "rgba(0,0,0,0.55)", margin: 0, lineHeight: 1.55 }}>
              Your cart is empty. Add something from the marketplace to get started.
            </p>
          ) : (
            <>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
                {cart.items.map((it) => {
                  const lineKey = `${it.lookup_key}|${it.chosen_version || ""}`;
                  return (
                  <div
                    key={lineKey}
                    data-testid={`cart-line-${lineKey}`}
                    style={{ display: "flex", gap: 12, alignItems: "center", padding: 10, borderRadius: 10, background: "#fafafa", border: "1px solid rgba(0,0,0,0.05)" }}
                  >
                    {it.cover_image && (
                      <img src={it.cover_image} alt="" style={{ width: 44, height: 44, borderRadius: 8, objectFit: "cover", flexShrink: 0 }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: "0.88rem", fontWeight: 600, lineHeight: 1.3, marginBottom: 2, color: "#111" }}>
                        {it.name}{it.chosen_version ? ` · ${it.chosen_version}` : ""}
                      </div>
                      <div style={{ fontSize: "0.82rem", color: "rgba(0,0,0,0.55)" }}>{formatMoney(it.unit_amount, it.currency)}</div>
                    </div>
                    <IconButton
                      testid={`cart-remove-${lineKey}`}
                      onClick={() => cart.removeItem(it.lookup_key, it.chosen_version)}
                      label={`Remove ${it.name}`}
                      size={32}
                      variant="ghost"
                    >
                      <Trash2 size={15} />
                    </IconButton>
                  </div>
                );})}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 4px", borderTop: "1px solid rgba(0,0,0,0.08)", marginBottom: 12 }}>
                <span style={{ fontSize: "0.85rem", color: "rgba(0,0,0,0.55)" }}>Subtotal</span>
                <span data-testid="cart-subtotal" style={{ fontSize: "1rem", fontWeight: 700, color: "#111" }}>{formatMoney(cart.subtotal, cart.currency)}</span>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <SecondaryButton testid="cart-clear-button" onClick={() => cart.clearCart()}>Clear</SecondaryButton>
                  <PrimaryButton testid="cart-checkout-button" onClick={() => { cart.closeDrawer(); navigate("/checkout"); }}>
                    Checkout <ArrowRight size={13} />
                  </PrimaryButton>
              </div>
            </>
          )}
        </Drawer>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// ACCOUNT
// -----------------------------------------------------------------------------
function AccountButton({ isMobile }) {
  const navigate = useNavigate();
  const { user, isAuthenticated, openSignIn, signOut, deleteAccount } = useShopAuth();
  const [open, setOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const panelRef = useRef(null);
  const closeTimer = useRef(null);
  const size = isMobile ? 44 : 40;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) { setOpen(false); setConfirmDelete(false); } };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (!isAuthenticated) {
    // Text + person icon, Horace-inspired. Compact on mobile.
    return (
      <button
        data-testid="account-signin-button"
        onClick={() => openSignIn("signin")}
        aria-label="Sign in"
        style={{
          height: size, padding: "0 16px 0 12px", borderRadius: 12,
          background: "rgba(15,23,42,0.9)",
          border: "1px solid rgba(255,255,255,0.14)",
          color: "#fff",
          display: "inline-flex", alignItems: "center", gap: 8,
          cursor: "pointer",
          fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase",
          fontFamily: "inherit",
          boxShadow: "0 6px 20px rgba(15,23,42,0.35)",
          transition: "transform 0.2s ease, background 0.2s ease",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.background = "rgba(15,23,42,1)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.background = "rgba(15,23,42,0.9)"; }}
      >
        <User size={16} strokeWidth={2.2} />
        Sign in
      </button>
    );
  }

  // Single-letter initial. When the user has a Google/upload picture we
  // use that instead; initials are the text fallback for accounts without
  // an avatar image.
  const initials = ((user?.name || user?.email || "?").trim().charAt(0) || "?").toUpperCase();

  // Hover-to-open (parity with the cart drawer trigger). A small close delay
  // keeps the menu open when the pointer travels the gap between the avatar
  // and the dropdown panel.
  const openMenu = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } setOpen(true); };
  const scheduleClose = () => { if (closeTimer.current) clearTimeout(closeTimer.current); closeTimer.current = setTimeout(() => { setOpen(false); setConfirmDelete(false); }, 220); };

  return (
    <div
      style={{ position: "relative" }}
      onMouseEnter={openMenu}
      onMouseLeave={scheduleClose}
      onFocus={openMenu}
      onBlur={scheduleClose}
    >
      <button
        data-testid="account-menu-button"
        onClick={() => (open ? setOpen(false) : openMenu())}  // click still works for touch
        aria-label="Account menu"
        aria-expanded={open}
        style={{
          width: size, height: size, borderRadius: "50%",
          background: BRAND_BLUE, color: "#fff",
          border: "2px solid rgba(255,255,255,0.85)",
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer",
          fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.02em",
          fontFamily: "inherit",
          boxShadow: "0 6px 20px rgba(15,23,42,0.35)",
          padding: 0, overflow: "hidden",
          transition: "transform 0.2s ease",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
      >
        {user?.picture ? (
          <img src={user.picture} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : initials}
      </button>
      {open && (
        <Drawer refEl={panelRef} testid="account-drawer" size={size} isMobile={isMobile}>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: "0.98rem", fontWeight: 700, color: "#111" }}>{user?.name || user?.email}</div>
            <div style={{ fontSize: "0.8rem", color: "rgba(0,0,0,0.55)" }}>{user?.email}</div>
          </div>
          <MenuLink testid="account-library" icon={<Library size={15} />} onClick={() => { setOpen(false); navigate("/library"); }}>My Library</MenuLink>
          <MenuLink testid="account-signout" icon={<LogOut size={15} />} onClick={async () => { await signOut(); setOpen(false); }}>Sign Out</MenuLink>
          <div style={{ height: 1, background: "rgba(0,0,0,0.08)", margin: "10px 0" }} />
          {!confirmDelete ? (
            <MenuLink testid="account-delete-open" icon={<ShieldOff size={15} />} tone="danger" onClick={() => setConfirmDelete(true)}>Delete Account</MenuLink>
          ) : (
            <div style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.28)", borderRadius: 8, padding: 10 }}>
              <p style={{ fontSize: "0.82rem", color: "#b91c1c", margin: "0 0 8px", lineHeight: 1.5 }}>
                Delete your account? Your download library will be removed but your purchases stay on Stripe&apos;s records.
              </p>
              <div style={{ display: "flex", gap: 6 }}>
                <SecondaryButton testid="account-delete-cancel" onClick={() => setConfirmDelete(false)}>Cancel</SecondaryButton>
                <PrimaryButton testid="account-delete-confirm" tone="danger" onClick={async () => { await deleteAccount(); setOpen(false); setConfirmDelete(false); }}>Delete</PrimaryButton>
              </div>
            </div>
          )}
        </Drawer>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// primitives
// -----------------------------------------------------------------------------
function IconButton({ children, testid, onClick, label, size = 40, variant = "solid" }) {
  const solid = variant === "solid";
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      aria-label={label}
      style={{
        width: size, height: size, borderRadius: solid ? 10 : 8,
        background: solid ? "rgba(15,23,42,0.9)" : "transparent",
        border: solid ? "1px solid rgba(255,255,255,0.12)" : "none",
        color: solid ? "#fff" : "#94a3b8",
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", position: "relative",
        boxShadow: solid ? "0 6px 20px rgba(15,23,42,0.35)" : "none",
        transition: "transform 0.2s ease, color 0.2s ease, background 0.2s ease",
        padding: 0,
      }}
      onMouseEnter={(e) => {
        if (solid) e.currentTarget.style.transform = "translateY(-1px)";
        else { e.currentTarget.style.color = "#fca5a5"; e.currentTarget.style.background = "rgba(239,68,68,0.1)"; }
      }}
      onMouseLeave={(e) => {
        if (solid) e.currentTarget.style.transform = "translateY(0)";
        else { e.currentTarget.style.color = "#94a3b8"; e.currentTarget.style.background = "transparent"; }
      }}
    >
      {children}
    </button>
  );
}

function Badge({ children, testid }) {
  return (
    <span
      data-testid={testid}
      style={{
        position: "absolute", top: -6, right: -6,
        minWidth: 20, height: 20, padding: "0 6px",
        borderRadius: 999, background: BRAND_BLUE, color: "#fff",
        fontSize: 11, fontWeight: 800,
        display: "flex", alignItems: "center", justifyContent: "center",
        border: "2px solid #0f172a",
      }}
    >{children}</span>
  );
}

function Drawer({ children, refEl, testid, size, isMobile }) {
  return (
    <div
      ref={refEl}
      data-testid={testid}
      style={{
        // On mobile: pop out of the cart-button anchor and go full-width
        // just below the fixed top bar. Content pane scrolls internally
        // if the cart grows past ~50% of the viewport.
        // On desktop: keep the dropdown anchored to the cart icon.
        position: isMobile ? "fixed" : "absolute",
        top: isMobile ? 76 : size + 12,
        right: isMobile ? 12 : 0,
        left: isMobile ? 12 : "auto",
        width: isMobile ? "auto" : 320,
        maxWidth: isMobile ? "none" : 360,
        maxHeight: isMobile ? "calc(100dvh - 90px)" : "auto",
        overflowY: isMobile ? "auto" : "visible",
        WebkitOverflowScrolling: "touch",
        background: "#ffffff",
        border: "1px solid rgba(0,0,0,0.08)",
        borderRadius: 14,
        padding: 18,
        boxShadow: "0 30px 80px rgba(15,23,42,0.18)",
        color: "#111",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        animation: "shopcluster-fadein 0.2s ease",
      }}
    >
      {children}
      <style>{`@keyframes shopcluster-fadein { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
}

function DrawerHeader({ label, onClose, testidClose }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
      <span style={{ fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.24em", textTransform: "uppercase", color: BRAND_PINK }}>{label}</span>
      <button data-testid={testidClose} onClick={onClose} aria-label="Close" style={{ background: "transparent", border: "none", color: "rgba(0,0,0,0.55)", cursor: "pointer", padding: 4 }}>
        <X size={16} />
      </button>
    </div>
  );
}

function MenuLink({ children, icon, onClick, testid, tone }) {
  const color = tone === "danger" ? "#b91c1c" : "#111";
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "10px 12px", width: "100%",
        background: "transparent", border: "none",
        color, cursor: "pointer", fontSize: "0.9rem",
        borderRadius: 8, textAlign: "left",
        fontFamily: "inherit",
        transition: "background 0.15s ease",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = tone === "danger" ? "rgba(239,68,68,0.08)" : "rgba(0,0,0,0.04)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
    >
      <span style={{ color: tone === "danger" ? "#b91c1c" : "rgba(0,0,0,0.55)" }}>{icon}</span>
      {children}
    </button>
  );
}

function PrimaryButton({ children, onClick, testid, tone }) {
  const bg = tone === "danger" ? "#ef4444" : "#111";
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      style={{
        flex: 1, padding: "10px 14px", borderRadius: 8,
        background: bg, color: "#fff", border: `1px solid ${bg}`,
        fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase",
        cursor: "pointer", fontFamily: "inherit",
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
        transition: "background 0.2s ease",
      }}
    >{children}</button>
  );
}

function SecondaryButton({ children, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      style={{
        flex: "0 0 auto", padding: "10px 14px", borderRadius: 8,
        background: "#ffffff", color: "#111",
        border: "1px solid rgba(0,0,0,0.14)",
        fontSize: "0.7rem", fontWeight: 700,
        letterSpacing: "0.18em", textTransform: "uppercase",
        cursor: "pointer", fontFamily: "inherit",
        transition: "background 0.2s ease, border-color 0.2s ease",
      }}
    >{children}</button>
  );
}

/*
 * Two icon buttons rendered side-by-side, styled identically to the top-right
 * cluster (dark pill for signed-out sign-in, blue avatar circle when signed
 * in, cart icon). Clicking acts directly — no top-right dropdown.
 */
export function MobileAccountMenuItem() {
  const { user, isAuthenticated, openSignIn, signOut } = useShopAuth();
  const navigate = useNavigate();
  const cart = useCart();

  const iconBtn = (testid, onClick, ariaLabel, children, opts = {}) => (
    <button
      data-testid={testid}
      onClick={onClick}
      aria-label={ariaLabel}
      style={{
        width: 44, height: 44, borderRadius: opts.round ? "50%" : 10,
        background: opts.avatar ? BRAND_BLUE : "#111",
        border: opts.avatar ? "2px solid rgba(255,255,255,0.85)" : "1px solid #111",
        color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", padding: 0,
        fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.02em",
        fontFamily: "inherit", position: "relative",
        boxShadow: "0 6px 16px rgba(15,23,42,0.15)",
      }}
    >{children}</button>
  );

  // Single-letter initial (first character of name or email). Matches the
  // avatar in the top-right cluster so both spots always show "J", never "JR".
  const initials = ((user?.name || user?.email || "?").trim().charAt(0) || "?").toUpperCase();

  return (
    <div style={{ display: "flex", justifyContent: "center", gap: 14, marginTop: 32 }}>
      {iconBtn("menu-shop-checkout", () => navigate("/checkout"), "Cart / Checkout",
        <>
          <ShoppingCart size={18} strokeWidth={2.2} />
          {cart.items.length > 0 && (
            <span style={{
              position: "absolute", top: -6, right: -6,
              minWidth: 20, height: 20, padding: "0 6px", borderRadius: 999,
              background: BRAND_BLUE, color: "#fff", fontSize: 11, fontWeight: 800,
              display: "flex", alignItems: "center", justifyContent: "center",
              border: "2px solid #fff",
            }}>{cart.items.length}</span>
          )}
        </>
      )}
      {isAuthenticated
        ? <>
            {iconBtn(
              "menu-shop-library",
              () => navigate("/library"),
              "My Library",
              // Prefer the user's Google/upload avatar; fall back to the
              // single-letter initial so the menu mirrors the top-right icon.
              user?.picture
                ? <img src={user.picture} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} />
                : initials,
              { round: true, avatar: true },
            )}
            {iconBtn("menu-shop-signout", () => signOut(), "Sign out", <LogOut size={17} />)}
          </>
        : iconBtn("menu-shop-signin", () => openSignIn("signin"), "Sign in", <User size={18} strokeWidth={2.2} />)
      }
    </div>
  );
}
